import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import ShimmerButton from '@/components/lib/magicui/ShimmerButton';
import { useReducedMotion } from '@/hooks/useReducedMotion';

export default function AboutFinalCTA() {
  const prefersReducedMotion = useReducedMotion();
  const buttonRef = useRef<HTMLDivElement>(null);

  // Motion values for magnetic hover
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Spring animation for smooth movement
  const springConfig = { stiffness: 150, damping: 15 };
  const xSpring = useSpring(x, springConfig);
  const ySpring = useSpring(y, springConfig);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (prefersReducedMotion || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const offsetX = e.clientX - centerX;
    const offsetY = e.clientY - centerY;

    // Clamp to ±8px
    const clampedX = Math.max(-8, Math.min(8, offsetX * 0.15));
    const clampedY = Math.max(-8, Math.min(8, offsetY * 0.15));

    x.set(clampedX);
    y.set(clampedY);
  };

  const handleMouseLeave = () => {
    if (prefersReducedMotion) return;
    x.set(0);
    y.set(0);
  };

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as const,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const headingItemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' as const },
    },
  };

  const ctaVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' as const, delay: 0.4 },
    },
  };

  return (
    <motion.section
      id="get-started"
      aria-label="Final call to action to work with Attain Domains"
      variants={sectionVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      className="relative w-full py-16 md:py-20 bg-gradient-to-r from-[#0000FF] via-blue-600 to-slate-900 overflow-hidden"
    >
      {/* Decorative overlays */}
      <div className="pointer-events-none absolute top-1/2 left-1/4 w-96 h-96 bg-[#0000FF]/30 rounded-full blur-3xl opacity-50" aria-hidden="true" />
      <div className="pointer-events-none absolute top-1/2 right-1/4 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl opacity-50" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Heading + copy block */}
          <motion.div
            variants={headingVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="text-center space-y-6 mb-8"
          >
            <motion.div variants={headingItemVariants}>
              <p className="text-xs md:text-sm font-semibold tracking-wide text-blue-100/80 uppercase mb-3">
                READY WHEN YOU ARE
              </p>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
                Your domain is ready. Is your business?
              </h2>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <p className="text-base md:text-lg text-blue-100/90 leading-relaxed max-w-3xl mx-auto">
                If you're serious about turning a strong domain into a client-getting service
                business, the next step is simple: we'll map your goals, walk through the Attain
                Protocol, and decide together if it's the right time to build.
              </p>
            </motion.div>
          </motion.div>

          {/* CTA buttons */}
          <motion.div
            variants={ctaVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8"
          >
            {/* Primary CTA with magnetic hover */}
            <motion.div
              ref={buttonRef}
              style={{ x: xSpring, y: ySpring }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              className="inline-block"
            >
              <Link to="/contact">
                <ShimmerButton
                  shimmerColor="#ffffff"
                  background="rgba(255, 255, 255, 0.95)"
                  borderRadius="0.75rem"
                  className="px-8 py-4 text-base md:text-lg font-semibold text-[#0000FF]"
                >
                  Book your free consult
                </ShimmerButton>
              </Link>
            </motion.div>

            {/* Secondary CTA */}
            <Link
              to="/services"
              className="text-blue-100/90 underline underline-offset-4 hover:text-white transition-colors duration-200 text-base md:text-lg font-medium"
            >
              View services
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

